import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { getTicketById, updateTicket, addComment, getConsultants, getGroups, escalateTicket, clearEscalation, getAttachments, uploadAttachment, deleteAttachment, getSLAStatus, pauseSLA, resumeSLA, acceptTicket, resolveWithKB, createSubTicket, softDeleteTicket } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format, formatDistanceToNow } from 'date-fns';
import Swal from 'sweetalert2';
import SLATimer from '../components/SLATimer';
import AIAssistantPanel from '../components/ai/AIAssistantPanel';
import {
  ArrowLeft, User, Clock, AlertTriangle, CheckCircle, Send,
  Lock, Globe, UserCheck, AlertCircle, RotateCcw, Zap,
  ChevronDown, MessageSquare, Activity, Paperclip, FileText,
  Upload, Trash2, Download, X, PauseCircle, PlayCircle, Shield,
  GitBranch, Plus, ExternalLink, Pencil, Building2, Factory, Mic,
} from 'lucide-react';
import NewTicketPage from './NewTicketPage';

// Basic HTML sanitizer for rendering rich-text ticket descriptions.
const sanitizeHtml = (html = '') =>
  String(html)
    .replace(/<\s*(script|style|iframe|object|embed)[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/(href|src)\s*=\s*("|')\s*javascript:[^"']*\2/gi, '$1=$2#$2');
const looksLikeHtml = (s = '') => /<[a-z][\s\S]*>/i.test(s);

/* ─── Status ─────────────────────────────────────────────────── */
const STATUS_STEPS = ['open','assigned','in_progress','pending','resolved','closed'];
const STATUS_META = {
  open:        { label: 'Open',        color: '#2563eb', bg: '#dbeafe' },
  assigned:    { label: 'Assigned',    color: '#d97706', bg: '#fef3c7' },
  in_progress: { label: 'In Progress', color: 'var(--primary)', bg: 'var(--primary-100)' },
  pending:     { label: 'Pending',     color: '#ea580c', bg: '#ffedd5' },
  resolved:    { label: 'Resolved',    color: '#16a34a', bg: '#dcfce7' },
  closed:      { label: 'Closed',      color: '#6b7280', bg: '#f3f4f6' },
  reopened:    { label: 'Reopened',    color: '#dc2626', bg: '#fee2e2' },
};
const PRIORITY_META = {
  critical: { label: 'P1 — Critical', color: '#dc2626', bg: '#fee2e2', dot: '#dc2626' },
  high:     { label: 'P2 — High',     color: '#ea580c', bg: '#ffedd5', dot: '#ea580c' },
  medium:   { label: 'P3 — Medium',   color: '#d97706', bg: '#fef3c7', dot: '#d97706' },
  low:      { label: 'P4 — Low',      color: '#16a34a', bg: '#dcfce7', dot: '#16a34a' },
};
const ROLE_COLOR = { admin: '#ef4444', consultant: '#f59e0b', user: 'var(--primary)' };
const TIER_COLOR = { L1: '#3b82f6', L2: '#10b981', L3: '#f59e0b', L4: '#ef4444' };

/* ─── Avatar ─────────────────────────────────────────────────── */
function Av({ name, role, size = 32 }) {
  const ini = name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: ROLE_COLOR[role] || '#6b7280',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 700, color: '#fff',
    }}>{ini}</div>
  );
}

/* ─── Info row (right panel) ─────────────────────────────────── */
function InfoRow({ label, value, mono }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
      <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text-main)', fontWeight: 500, textAlign: 'right', wordBreak: 'break-word', fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>
        {value || '—'}
      </span>
    </div>
  );
}

/* ─── Status badge ───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: m.bg, color: m.color }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color, display: 'inline-block' }} />
      {m.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: m.bg, color: m.color }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, display: 'inline-block' }} />
      {m.label}
    </span>
  );
}

/* ─── Stepper ────────────────────────────────────────────────── */
function Stepper({ ticket, canEdit, onPatch, canClickStep, visibleSteps }) {
  const stepsToRender = visibleSteps || STATUS_STEPS;
  // If ticket is 'closed' but 'closed' is hidden from this role (consultant), highlight 'resolved' as current
  const displayStatus =
    visibleSteps && ticket.status === 'closed' && !visibleSteps.includes('closed')
      ? 'resolved'
      : ticket.status;
  const currentIdx = stepsToRender.indexOf(displayStatus);
  const isReopened = ticket.status === 'reopened';

  return (
    <div className="stepper">
      {stepsToRender.map((s, i) => {
        const meta  = STATUS_META[s];
        const done  = i < currentIdx;
        const active = i === currentIdx && !isReopened;
        // If canClickStep is provided, only allow clicking permitted statuses
        const clickable = canEdit && s !== ticket.status && (!canClickStep || canClickStep(s));
        return (
          <React.Fragment key={s}>
            <div
              className="step-item"
              onClick={() => clickable && onPatch({ status: s })}
              title={clickable ? `Set to ${meta.label}` : meta.label}
              style={{ opacity: (!done && !active) ? 0.45 : 1, cursor: clickable ? 'pointer' : 'default' }}
            >
              <div className={`step-circle ${done ? 'done' : active ? 'active' : ''}`}>
                {done ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                ) : (
                  <span>{i + 1}</span>
                )}
              </div>
              <span className={`step-label ${done ? 'done' : active ? 'active' : ''}`}>
                {meta.label}
              </span>
            </div>
            {i < stepsToRender.length - 1 && (
              <div className={`step-line ${done ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
      {/* Reopened extra node */}
      {isReopened && (
        <>
          <div className="step-line" style={{ background: '#dc2626' }} />
          <div className="step-item">
            <div className="step-circle" style={{ borderColor: '#dc2626', color: '#dc2626', background: '#fee2e2', boxShadow: '0 0 0 4px rgba(220,38,38,0.15)' }}>
              <RotateCcw size={12} />
            </div>
            <span className="step-label active" style={{ color: '#dc2626' }}>Reopened</span>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════ MAIN PAGE ══════════════════════════ */
export default function TicketDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ticket,      setTicket]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [consultants, setConsultants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [assignGroup, setAssignGroup] = useState(''); // group filter for the assign dropdown
  const [updating,    setUpdating]    = useState(false);
  const [comment,     setComment]     = useState('');
  const [isInternal,  setIsInternal]  = useState(false);
  const [submittingC, setSubmittingC] = useState(false);
  const [error,       setError]       = useState('');
  const [activeTab,   setActiveTab]   = useState('comments');
  const [showAssign,  setShowAssign]  = useState(false);
  const [showEdit,    setShowEdit]    = useState(false);
  const [escalateModal, setEscalateModal] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  // Set by NewTicketPage when the ticket saved but its voice clip did not
  // upload, so the user finds out rather than silently losing the recording.
  const location = useLocation();
  const voiceUploadFailed = !!location.state?.voiceUploadFailed;
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [subModal, setSubModal]     = useState(false);
  const [subForm, setSubForm]       = useState({ title: '', description: '', ticket_type: 'incident', priority: 'medium', assigned_to: '' });
  const [subSubmitting, setSubSubmitting] = useState(false);
  const [attachments,   setAttachments]   = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragOver,      setDragOver]      = useState(false);
  const [slaData,       setSlaData]       = useState(null);
  const [slaUpdating,   setSlaUpdating]   = useState(false);
  // Resolve-with-KB modal
  const [resolveModal,  setResolveModal]  = useState(false);
  const [resolveForm,   setResolveForm]   = useState({ resolution_heading: '', root_cause: '', resolution_steps: '', kb_category: '' });
  const [resolving,     setResolving]     = useState(false);
  const [resolveSuccess, setResolveSuccess] = useState('');
  // Accept countdown
  const [acceptNow,     setAcceptNow]     = useState(false);

  // Role shortcuts — declared early so patch() can safely reference them
  const isAdmin      = user?.role === 'admin';
  const isConsultant = user?.role === 'consultant';
  const isUser       = user?.role === 'user';

  const fetchTicket = () => {
    setLoading(true);
    getTicketById(id)
      .then(r => setTicket(r.data))
      .catch(() => navigate('/tickets'))
      .finally(() => setLoading(false));
  };

  const fetchAttachments = () => {
    getAttachments(id).then(r => setAttachments(r.data || [])).catch(() => {});
  };

  const fetchSLA = () => {
    getSLAStatus(id).then(r => setSlaData(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchTicket();
    getConsultants().then(r => setConsultants(r.data || [])).catch(() => {});
    getGroups().then(r => setGroups(r.data || [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    fetchAttachments();
    fetchSLA();
    const iv = setInterval(fetchSLA, 60_000);
    return () => clearInterval(iv);
  // eslint-disable-next-line
  }, [id]);

  // Auto-populate the assign Group/Module filter from the ticket's group.
  useEffect(() => {
    if (ticket?.group_id) setAssignGroup(ticket.group_id);
  }, [ticket?.group_id]);

  const patch = async (fields) => {
    // Guard: consultant must go through the Resolve popup — cannot directly set resolved or closed
    if (isConsultant) {
      if (fields.status === 'resolved') {
        if (ticket) {
          setResolveForm({
            resolution_heading: ticket.title || '',
            root_cause: '',
            resolution_steps: '',
            kb_category: ticket.service_category || ticket.category || '',
          });
          setResolveModal(true);
        }
        return;
      }
      if (fields.status === 'closed') return; // Consultant cannot close tickets
    }
    setUpdating(true);
    setError('');
    try { await updateTicket(id, fields); fetchTicket(); }
    catch (err) {
      const data = err?.response?.data || {};
      const blocked = data.reason === 'checkout' || data.reason === 'leave';
      Swal.fire({
        icon: 'error',
        title: blocked
          ? (data.reason === 'leave' ? 'Consultant On Leave' : 'Consultant Checked Out')
          : 'Action Not Allowed',
        text: data.error || 'Update failed. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc2626',
        customClass: { popup: 'swal-popup-custom' },
      });
    }
    finally { setUpdating(false); }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSubmittingC(true);
    try {
      await addComment(id, { comment, is_internal: isInternal });
      setComment(''); setIsInternal(false); fetchTicket();
    } catch { setError('Failed to add comment.'); }
    finally { setSubmittingC(false); }
  };

  const handleEscalate = async () => {
    if (!escalateReason.trim()) return;
    try {
      await escalateTicket(id, { reason: escalateReason });
      setEscalateModal(false); setEscalateReason(''); fetchTicket();
    } catch { setError('Escalation failed.'); }
  };

  // Admin-only. The ticket goes to the Recycle Bin, not away for good, so the
  // confirm says as much — then we leave the page, since it no longer resolves.
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await softDeleteTicket(id);
      setDeleteModal(false);
      navigate('/tickets/deleted');
    } catch {
      setError('Failed to delete ticket.');
      setDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleClearEscalation = async () => {
    if (!window.confirm('Remove the escalation from this ticket?')) return;
    try {
      await clearEscalation(id);
      fetchTicket();
    } catch { setError('Failed to clear escalation.'); }
  };

  const handleCreateSubTicket = async () => {
    if (!subForm.title.trim() || !subForm.description.trim()) return;
    setSubSubmitting(true);
    try {
      await createSubTicket(id, {
        title: subForm.title,
        description: subForm.description,
        ticket_type: subForm.ticket_type,
        priority: subForm.priority,
        assigned_to: subForm.assigned_to || null,
      });
      setSubModal(false);
      setSubForm({ title: '', description: '', ticket_type: 'incident', priority: 'medium', assigned_to: '' });
      fetchTicket();
    } catch { setError('Failed to create sub-ticket.'); }
    finally { setSubSubmitting(false); }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await uploadAttachment(id, fd);
      fetchAttachments();
    } catch (e) {
      setError(e?.response?.data?.error || 'Upload failed.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSLAPause = async () => {
    setSlaUpdating(true);
    try { await pauseSLA(id, { reason: 'manual' }); fetchSLA(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to pause SLA.'); }
    finally { setSlaUpdating(false); }
  };

  const handleSLAResume = async () => {
    setSlaUpdating(true);
    try { await resumeSLA(id); fetchSLA(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to resume SLA.'); }
    finally { setSlaUpdating(false); }
  };

  const handleAccept = async () => {
    setAcceptNow(true);
    try {
      await acceptTicket(id);
      fetchTicket(); fetchSLA();
    } catch (e) { setError(e?.response?.data?.error || 'Failed to accept ticket.'); }
    finally { setAcceptNow(false); }
  };

  const handleResolveSubmit = async (e) => {
    e.preventDefault();
    if (!resolveForm.resolution_heading.trim() || !resolveForm.resolution_steps.trim()) return;
    setResolving(true);
    try {
      await resolveWithKB(id, { ...resolveForm, kb_category: resolveForm.kb_category || ticket.service_category || ticket.category || 'General' });
      setResolveModal(false);
      setResolveSuccess('Ticket resolved! Resolution saved to Knowledge Base. User and admin notified.');
      setResolveForm({ resolution_heading: '', root_cause: '', resolution_steps: '', kb_category: '' });
      fetchTicket(); fetchSLA();
    } catch (e) { setError(e?.response?.data?.error || 'Failed to resolve ticket.'); }
    finally { setResolving(false); }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await deleteAttachment(id, attachmentId);
      fetchAttachments();
    } catch { setError('Failed to delete attachment.'); }
  };

  /* ── Loading ── */
  if (loading) return (
    <AppLayout pageTitle="Ticket Detail">
      <div className="flex-center" style={{ height: 320 }}>
        <div className="spinner" style={{ width: 36, height: 36 }} />
      </div>
    </AppLayout>
  );
  if (!ticket) return null;

  const pm         = PRIORITY_META[ticket.priority]  || PRIORITY_META.medium;
  const isBreached = ticket.sla_due_at && new Date(ticket.sla_due_at) < new Date() && !['resolved','closed'].includes(ticket.status);
  const canEdit    = isAdmin || isConsultant;
  const isMyTicket   = ticket.assigned_to === user?.id;
  // Consultant can accept their assigned ticket any time it hasn't been accepted yet
  // and isn't already resolved/closed (not only while status is exactly 'assigned').
  const needsAccept  = isConsultant && isMyTicket && !ticket.accepted_at && !['resolved', 'closed'].includes(ticket.status);
  const acceptDeadline = ticket.accept_deadline ? new Date(ticket.accept_deadline) : null;
  const acceptMinsLeft = acceptDeadline ? Math.max(0, Math.floor((acceptDeadline - Date.now()) / 60000)) : null;
  const acceptOverdue  = ticket.acceptance_overdue || (acceptDeadline && acceptDeadline < new Date());
  // Priority-based acceptance window label (critical 15 min · high 30 min · medium 2 hr · low 4 hr)
  const acceptWindowLabel = ticket.accept_window_label || '15 minutes';
  const notifyEmails = ticket.notify_emails ? ticket.notify_emails.split(',').map(s => s.trim()).filter(Boolean) : [];
  const tierColor  = TIER_COLOR[ticket.support_tier] || '#6b7280';

  // Split the one attachment list into recordings and everything else, so each
  // tab shows only its own items and the counts don't double up.
  const voiceNotes      = attachments.filter(a => a.is_voice_note);
  const fileAttachments = attachments.filter(a => !a.is_voice_note);

  const TABS = [
    { key: 'details',     label: 'Details',          Icon: FileText },
    { key: 'comments',    label: 'Comments',          Icon: MessageSquare, count: ticket.comments?.length },
    { key: 'activity',    label: 'Activity',          Icon: Activity,      count: ticket.history?.length  },
    // Voice notes are stored as attachments but get their own tab, so a
    // recording isn't buried among screenshots and documents.
    { key: 'attachments', label: 'Attachments',       Icon: Paperclip,     count: fileAttachments.length || undefined },
    { key: 'voice',       label: 'Voice Message',     Icon: Mic,           count: voiceNotes.length || undefined },
    { key: 'audit',       label: 'Audit Trail',       Icon: AlertCircle },
  ];

  return (
    <AppLayout pageTitle={ticket.ticket_number}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1200, margin: '0 auto' }}>

        {/* ── TOP ACTION BAR ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => navigate('/tickets')} style={{ gap: 6 }}>
            <ArrowLeft size={14} /> Back
          </button>
          <div style={{ flex: 1 }} />

          {/* Update Ticket — admin only: edit the full ticket in a popup */}
          {isAdmin && !['resolved','closed'].includes(ticket.status) && (
            <button className="btn btn-outline btn-sm" style={{ color: '#2563eb', borderColor: '#93c5fd' }} onClick={() => setShowEdit(true)}>
              <Pencil size={14} /> Update Ticket
            </button>
          )}

          {/* Action buttons */}
          {canEdit && !ticket.assigned_to && (
            <button className="btn btn-outline btn-sm" onClick={() => setShowAssign(true)}>
              <UserCheck size={14} /> Assign
            </button>
          )}
          {/* Accept button — consultant only, while status is 'assigned' and unaccepted */}
          {needsAccept && (
            <button
              className="btn btn-sm"
              disabled={acceptNow}
              onClick={handleAccept}
              style={{
                background: acceptOverdue ? '#dc2626' : '#16a34a',
                color: '#fff', border: 'none', gap: 6,
                animation: acceptOverdue ? 'sla-pulse 1.2s ease-in-out infinite' : 'none',
              }}
            >
              <CheckCircle size={14} />
              {acceptNow ? 'Accepting…' : acceptOverdue ? '⚠️ Accept Now (Overdue)' : `Accept Ticket${acceptMinsLeft !== null ? ` (${acceptMinsLeft}m left)` : ''}`}
            </button>
          )}

          {canEdit && ticket.status === 'pending' && !(isConsultant && slaData?.is_paused) && (
            <button className="btn btn-success btn-sm" onClick={() => patch({ status: 'in_progress' })} style={{ gap: 6 }}>
              <PlayCircle size={14} /> Resume Work
            </button>
          )}

          {/* Resolve button — consultant opens popup; admin resolves directly */}
          {canEdit && !['resolved','closed'].includes(ticket.status) && !(isConsultant && slaData?.is_paused) && (
            isConsultant ? (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  setResolveForm({ resolution_heading: ticket.title, root_cause: '', resolution_steps: '', kb_category: ticket.service_category || ticket.category || '' });
                  setResolveModal(true);
                }}
                style={{ gap: 6 }}
              >
                <CheckCircle size={14} /> Resolve
              </button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => patch({ status: 'resolved' })} style={{ gap: 6 }}>
                <CheckCircle size={14} /> Resolve
              </button>
            )
          )}

          {(isAdmin || isUser) && !ticket.escalated && (
            <button className="btn btn-outline btn-sm" style={{ color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => setEscalateModal(true)}>
              <AlertTriangle size={14} /> Escalate
            </button>
          )}
          {isAdmin && ticket.escalated && (
            <button className="btn btn-outline btn-sm" style={{ color: '#6b7280', borderColor: '#d1d5db' }} onClick={handleClearEscalation}>
              <AlertTriangle size={14} /> Clear Escalation
            </button>
          )}
          {/* Sub-ticket: admin always, consultant if assigned to this ticket */}
          {(isAdmin || (isConsultant && ticket.assigned_to === user?.id)) && !ticket.parent_ticket_id && (
            <button className="btn btn-outline btn-sm" style={{ color: '#7c3aed', borderColor: '#c4b5fd' }} onClick={() => setSubModal(true)}>
              <GitBranch size={14} /> Add Sub-ticket
            </button>
          )}
          {/* Delete: admin only. Moves the ticket to the Recycle Bin — it can
              be restored from Ticket History → Deleted Tickets. */}
          {isAdmin && (
            <button
              className="btn btn-outline btn-sm"
              style={{ color: '#dc2626', borderColor: '#fca5a5' }}
              onClick={() => setDeleteModal(true)}
            >
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>

        {voiceUploadFailed && (
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13,
            padding: '10px 14px', borderRadius: 8, marginBottom: 14,
            background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
          }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              The ticket was created, but the voice message could not be uploaded.
              Everything else was saved — please add the recording again if it is needed.
            </span>
          </div>
        )}

        {error         && <div className="alert alert-error">{error}</div>}
        {resolveSuccess && (
          <div style={{ padding: '12px 16px', borderRadius: 10, background: '#f0fdf4', border: '1.5px solid #86efac', color: '#166534', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={16} color="#16a34a" /> {resolveSuccess}
          </div>
        )}

        {/* 15-minute acceptance warning banner */}
        {needsAccept && (
          <div style={{
            padding: '12px 16px', borderRadius: 10,
            background: acceptOverdue ? '#fee2e2' : '#fef3c7',
            border: `1.5px solid ${acceptOverdue ? '#fca5a5' : '#fcd34d'}`,
            color: acceptOverdue ? '#991b1b' : '#92400e',
            fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            animation: acceptOverdue ? 'sla-pulse 1.5s ease-in-out infinite' : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={16} />
              {acceptOverdue
                ? `⚠️ You have not accepted this ticket. It was assigned over ${acceptWindowLabel} ago. Admin has been notified. Please accept or escalate immediately.`
                : `⏱️ You have been assigned this ticket. Please accept within ${acceptMinsLeft} minute${acceptMinsLeft !== 1 ? 's' : ''} to confirm you are working on it.`
              }
            </div>
            <button
              onClick={handleAccept}
              disabled={acceptNow}
              style={{ flexShrink: 0, padding: '6px 16px', borderRadius: 7, background: acceptOverdue ? '#dc2626' : '#d97706', color: '#fff', border: 'none', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
            >
              {acceptNow ? 'Accepting…' : 'Accept Now'}
            </button>
          </div>
        )}

        {/* Admin warning when consultant hasn't accepted on time */}
        {isAdmin && ticket.acceptance_overdue && !ticket.accepted_at && !['resolved','closed'].includes(ticket.status) && (
          <div style={{ padding: '12px 16px', borderRadius: 10, background: '#fff7ed', border: '1.5px solid #fed7aa', color: '#9a3412', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} />
            <strong>Warning:</strong> The assigned consultant has not accepted this ticket within {acceptWindowLabel}. Consider reassigning.
          </div>
        )}

        {/* ── AI ASSISTANT PANEL (additive; hidden unless AI enabled) ── */}
        <AIAssistantPanel
          ticket={ticket}
          canApprove={isAdmin || isConsultant}
          onApplied={fetchTicket}
        />

        {/* ── TICKET HEADER ── */}
        <div className="card" style={{ padding: '20px 24px' }}>
          {/* Badge row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-light)', padding: '3px 10px', borderRadius: 6 }}>
              {ticket.ticket_number}
            </span>
            {ticket.support_tier && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: tierColor + '18', color: tierColor, border: `1px solid ${tierColor}30` }}>
                {ticket.support_tier}
              </span>
            )}
            <PriorityBadge priority={ticket.priority} />
            <StatusBadge   status={ticket.status} />
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: '#f3f4f6', color: '#6b7280', textTransform: 'capitalize' }}>
              {ticket.ticket_type?.replace(/_/g, ' ')}
            </span>
            {ticket.is_from_zyra && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6, background: '#fef3c7', color: '#92400e' }}>⚡ Zyra</span>
            )}
            {slaData && !slaData.no_sla && (
              <SLATimer ticketId={id} priority={ticket.priority} compact />
            )}
            {ticket.escalated && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: '#fce7f3', color: '#be185d' }}>
                🔺 Escalated
              </span>
            )}
          </div>

          {/* Title */}
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-main)', marginBottom: 8, lineHeight: 1.4 }}>
            {ticket.title}
          </h2>

          {/* Reporter info */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: ticket.requestor_id ? 8 : 20 }}>
            <User size={13} />
            <span>{ticket.requestor_id && ticket.requestor_id !== ticket.raised_by ? 'Created by (Admin)' : 'Reported by'}</span>
            <span style={{ fontWeight: 600, color: 'var(--text-sub)' }}>{ticket.raised_by_name}</span>
            <span>·</span>
            <Clock size={13} />
            <span>{format(new Date(ticket.created_at), 'MMM d, yyyy HH:mm')}</span>
            {ticket.contact_number && (
              <><span>·</span><span>{ticket.contact_number}</span></>
            )}
            {ticket.site && (
              <><span>·</span><span>{ticket.site}</span></>
            )}
          </div>

          {/* Parent ticket banner — shown when this is a sub/child ticket */}
          {ticket.parentTicket && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
              padding: '9px 14px', borderRadius: 8, marginBottom: 12,
              background: '#eff6ff', border: '1px solid #93c5fd', cursor: 'pointer',
            }} onClick={() => navigate(`/tickets/${ticket.parent_ticket_id}`)}>
              <GitBranch size={14} color="#2563eb" />
              <span style={{ color: '#1e40af', fontWeight: 500 }}>Part of parent ticket</span>
              <span style={{ fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', padding: '2px 10px', borderRadius: 6, fontSize: 12 }}>
                {ticket.parentTicket.ticket_number}
              </span>
              <span style={{ color: '#3b82f6', flex: 1 }}>{ticket.parentTicket.title}</span>
              <ExternalLink size={12} color="#93c5fd" />
            </div>
          )}

          {/* Requestor row — shown when admin created ticket on behalf of a user */}
          {ticket.requestor_id && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
              padding: '8px 14px', borderRadius: 8, marginBottom: 20,
              background: '#f0fdf4', border: '1px solid #86efac',
            }}>
              <User size={13} color="#16a34a" />
              <span style={{ color: '#166534', fontWeight: 500 }}>Requestor</span>
              <span style={{ fontWeight: 700, color: '#15803d' }}>{ticket.requestor_name}</span>
              {(ticket.requestor_department || ticket.requestor_role) && (
                <span style={{ color: '#16a34a', fontSize: 12 }}>· Role: {ticket.requestor_department || ticket.requestor_role}</span>
              )}
              {ticket.requestor_email && (
                <span style={{ color: '#16a34a', fontSize: 12 }}>· {ticket.requestor_email}</span>
              )}
            </div>
          )}

          {/* Originating company / plant — sent by Zyra ERP so tickets can be
              filtered by company. Older tickets predate this and have neither. */}
          {ticket.company_name && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, flexWrap: 'wrap',
              padding: '8px 14px', borderRadius: 8, marginBottom: 20,
              background: '#eff6ff', border: '1px solid #93c5fd',
            }}>
              <Building2 size={13} color="#2563eb" />
              <span style={{ color: '#1e40af', fontWeight: 500 }}>Company</span>
              <span style={{ fontWeight: 700, color: '#1d4ed8' }}>{ticket.company_name}</span>
              {ticket.company_code && (
                <span style={{ color: '#3b82f6', fontSize: 12 }}>({ticket.company_code})</span>
              )}
              {ticket.plant_name && (
                <>
                  <span style={{ color: '#bfdbfe' }}>|</span>
                  <Factory size={13} color="#2563eb" />
                  <span style={{ color: '#1e40af', fontWeight: 500 }}>Plant</span>
                  <span style={{ fontWeight: 700, color: '#1d4ed8' }}>{ticket.plant_name}</span>
                  {ticket.plant_code && (
                    <span style={{ color: '#3b82f6', fontSize: 12 }}>({ticket.plant_code})</span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Status Stepper */}
          {/* Consultant: 'Closed' step is hidden entirely; resolved opens popup via patch guard */}
          <Stepper
            ticket={ticket}
            canEdit={canEdit}
            onPatch={patch}
            visibleSteps={isConsultant
              ? ['open', 'assigned', 'in_progress', 'pending', 'resolved']
              : undefined}
            canClickStep={isConsultant
              ? (s) => ['in_progress', 'pending', 'resolved'].includes(s)
              : undefined}
          />
        </div>

        {/* ── MAIN 2-COL LAYOUT ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, alignItems: 'start' }}>

          {/* ═══ LEFT ═══ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Tabs + Content card */}
            <div className="card" style={{ overflow: 'hidden' }}>
              <div className="tabs-bar" style={{ padding: '0 20px' }}>
                {TABS.map(t => (
                  <button
                    key={t.key}
                    className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(t.key)}
                  >
                    <t.Icon size={14} />
                    {t.label}
                    {t.count != null && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: activeTab === t.key ? 'var(--primary-100)' : 'var(--gray-100)', color: activeTab === t.key ? 'var(--primary)' : 'var(--text-muted)' }}>
                        {t.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div style={{ padding: '20px' }}>

                {/* ── DETAILS TAB ── */}
                {activeTab === 'details' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Description */}
                    <div>
                      <div className="section-heading">Description</div>
                      {looksLikeHtml(ticket.description) ? (
                        <div
                          className="rte-render"
                          style={{ background: 'var(--gray-50)', borderRadius: 8, padding: '14px 16px', border: '1px solid var(--border)' }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(ticket.description) }}
                        />
                      ) : (
                        <div style={{ fontSize: 13.5, lineHeight: 1.8, color: 'var(--text-sub)', whiteSpace: 'pre-wrap', background: 'var(--gray-50)', borderRadius: 8, padding: '14px 16px', border: '1px solid var(--border)' }}>
                          {ticket.description}
                        </div>
                      )}
                    </div>

                    {/* Classification grid */}
                    <div>
                      <div className="section-heading">Classification</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px' }}>
                        {[
                          { label: 'ERP System',       value: ticket.service_category },
                          { label: 'Module',           value: ticket.category },
                          { label: 'Subcategory',      value: ticket.subcategory },
                          { label: 'Request Type',     value: ticket.ticket_type?.replace(/_/g,' ') },
                          { label: 'ERP Version',      value: ticket.erp_version },
                          { label: 'Affected Users',   value: ticket.affected_users_count ? `${ticket.affected_users_count} users` : null },
                          { label: 'Client',           value: ticket.project_name },
                          ticket.sap_transaction_code && { label: 'SAP T-Code', value: ticket.sap_transaction_code, mono: true },
                        ].filter(Boolean).map(({ label, value, mono }) => (
                          <div key={label}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
                            <div style={{ fontSize: 13.5, color: value ? 'var(--text-main)' : 'var(--text-muted)', fontStyle: value ? 'normal' : 'italic', fontFamily: mono ? 'var(--font-mono)' : 'inherit', fontWeight: value ? 500 : 400 }}>
                              {value || 'Not specified'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notify emails */}
                    {notifyEmails.length > 0 && (
                      <div>
                        <div className="section-heading">Email Notifications</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {notifyEmails.map(em => (
                            <span key={em} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20 }}>
                              ✉ {em}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Zyra details */}
                    {ticket.zyra_error_code && (
                      <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>⚡ Zyra Error Details</div>
                        <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: '#78350f' }}>
                          Code: <strong>{ticket.zyra_error_code}</strong> &nbsp;|&nbsp; Module: <strong>{ticket.zyra_module || '—'}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── COMMENTS TAB ── */}
                {activeTab === 'comments' && (
                  <div>
                    {/* Comment list */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                      {(!ticket.comments || ticket.comments.length === 0) && (
                        <div className="empty-state">
                          <MessageSquare size={32} color="var(--gray-300)" style={{ marginBottom: 10 }} />
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>No comments yet</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Be the first to add a comment</div>
                        </div>
                      )}
                      {ticket.comments?.map(c => {
                        const isMine = c.user_id === user?.id;
                        return (
                          <div key={c.id} style={{ display: 'flex', gap: 10, flexDirection: isMine ? 'row-reverse' : 'row' }}>
                            <Av name={c.user_name} role={c.user_role} size={30} />
                            <div style={{
                              maxWidth: '80%', padding: '10px 14px', borderRadius: 10,
                              background: c.is_internal ? '#fffbeb' : isMine ? 'var(--primary-light)' : 'var(--gray-50)',
                              border: `1px solid ${c.is_internal ? '#fde68a' : isMine ? 'var(--primary-200)' : 'var(--border)'}`,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-main)' }}>{c.user_name}</span>
                                <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'capitalize', background: 'var(--gray-100)', padding: '1px 6px', borderRadius: 8 }}>{c.user_role}</span>
                                {c.is_internal && (
                                  <span style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '1px 7px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Lock size={9} /> Internal
                                  </span>
                                )}
                                <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                                  {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                                </span>
                              </div>
                              <p style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--text-sub)', whiteSpace: 'pre-wrap', margin: 0 }}>{c.comment}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add comment */}
                    <form onSubmit={handleComment} style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                      <textarea
                        className="form-control"
                        rows={3} value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder="Write a comment…"
                        style={{ marginBottom: 10, resize: 'vertical' }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          {user?.role !== 'user' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
                              <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} style={{ accentColor: 'var(--primary)', width: 14, height: 14 }} />
                              <Lock size={12} /> Internal note (hidden from requester)
                            </label>
                          )}
                        </div>
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm"
                          disabled={submittingC || !comment.trim()}
                          style={{ gap: 6 }}
                        >
                          <Send size={13} />
                          {submittingC ? 'Sending…' : 'Add Comment'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* ── ACTIVITY TAB ── */}
                {activeTab === 'activity' && (
                  <div>
                    {(!ticket.history || ticket.history.length === 0) && (
                      <div className="empty-state">
                        <Activity size={32} color="var(--gray-300)" style={{ marginBottom: 10 }} />
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>No activity recorded</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {ticket.history?.map((h, i) => {
                        /* Resolve UUID → name (fallback for old records) */
                        const resolveVal = (val) => {
                          if (!val || val === 'null') return null;
                          if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(val)) {
                            const c = consultants.find(x => x.id === val);
                            return c ? c.name : val.slice(0, 8) + '…';
                          }
                          return val.replace(/_/g, ' ');
                        };
                        const isCreated = h.field_changed === 'created';
                        const dotColor  = isCreated ? '#16a34a' : 'var(--primary)';
                        return (
                          <div key={h.id} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: i < ticket.history.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                            {/* Timeline dot */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0, paddingTop: 4 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                              {i < ticket.history.length - 1 && (
                                <div style={{ flex: 1, width: 1, background: 'var(--border)', marginTop: 4 }} />
                              )}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.6 }}>
                                <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{h.changed_by_name || 'System'}</span>

                                {isCreated ? (
                                  /* ── "created" special entry ── */
                                  h.new_value ? (
                                    <> <span style={{ color: 'var(--text-muted)' }}>created this ticket on behalf of</span>{' '}
                                    <span style={{ fontWeight: 700, color: '#15803d', background: '#f0fdf4', padding: '1px 8px', borderRadius: 5, fontSize: 11 }}>
                                      {h.new_value}
                                    </span>
                                    <span style={{ color: 'var(--text-muted)', marginLeft: 4, fontSize: 11 }}>(requestor)</span></>
                                  ) : (
                                    <> <span style={{ color: 'var(--text-muted)' }}>created this ticket</span></>
                                  )
                                ) : (
                                  /* ── normal change entry ── */
                                  <>
                                    {' '}<span style={{ color: 'var(--text-muted)' }}>changed</span>{' '}
                                    <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
                                      {{ assigned_to: 'Assigned To', status: 'Status', priority: 'Priority', escalated: 'Escalated', support_tier: 'Support Tier', sub_ticket: 'Sub-ticket Added' }[h.field_changed] || h.field_changed}
                                    </span>
                                    {resolveVal(h.old_value) && (
                                      <>{' from '}<span style={{ background: '#f3f4f6', color: '#6b7280', padding: '1px 7px', borderRadius: 5, fontSize: 11 }}>
                                        {resolveVal(h.old_value)}
                                      </span></>
                                    )}
                                    {resolveVal(h.new_value) && (
                                      <>{' to '}<span style={{ background: 'var(--primary-light)', color: 'var(--primary)', padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>
                                        {resolveVal(h.new_value)}
                                      </span></>
                                    )}
                                  </>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                                {format(new Date(h.changed_at), 'dd MMM yyyy, HH:mm')}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── ATTACHMENTS TAB ── */}
                {activeTab === 'attachments' && (
                  <div style={{ padding: '4px 0' }}>
                    {/* Drop zone */}
                    <div
                      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files[0]); }}
                      onClick={() => document.getElementById('att-file-input').click()}
                      style={{
                        border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
                        borderRadius: 10, padding: '24px 16px', textAlign: 'center',
                        cursor: uploadingFile ? 'not-allowed' : 'pointer',
                        background: dragOver ? 'var(--primary-50, #ede9fe)' : 'var(--bg-body)',
                        transition: 'all 0.15s', marginBottom: 16,
                      }}
                    >
                      {uploadingFile ? (
                        <div className="spinner" style={{ width: 28, height: 28, margin: '0 auto 8px' }} />
                      ) : (
                        <Upload size={28} color="var(--primary)" style={{ marginBottom: 8 }} />
                      )}
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)' }}>
                        {uploadingFile ? 'Uploading…' : 'Click or drag & drop a file'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                        PDF, Word, Excel, Images, ZIP — max 10 MB
                      </div>
                      <input
                        id="att-file-input"
                        type="file"
                        style={{ display: 'none' }}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.zip"
                        onChange={e => handleFileUpload(e.target.files[0])}
                      />
                    </div>

                    {/* File list — voice notes live in their own tab */}
                    {fileAttachments.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                        No attachments yet
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {fileAttachments.map(att => {
                          const isImage = att.file_type?.startsWith('image/');
                          const sizeKB = att.file_size ? (att.file_size / 1024).toFixed(1) : '—';
                          const fileUrl = `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${att.file_url}`;
                          return (
                            <div key={att.id} style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '10px 12px', borderRadius: 8,
                              border: '1px solid var(--border)', background: 'var(--bg-card)',
                            }}>
                              {isImage ? (
                                <img src={fileUrl} alt={att.file_name}
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                              ) : (
                                <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <FileText size={20} color="var(--primary)" />
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {att.file_name}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                  {sizeKB} KB · {att.uploaded_by_name} · {format(new Date(att.created_at), 'dd MMM yyyy, HH:mm')}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <a href={fileUrl} download={att.file_name} target="_blank" rel="noreferrer"
                                  style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-sub)', display: 'flex', alignItems: 'center' }}>
                                  <Download size={14} />
                                </a>
                                <button onClick={() => handleDeleteAttachment(att.id)}
                                  style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── VOICE MESSAGE TAB ── */}
                {activeTab === 'voice' && (
                  <div style={{ padding: '4px 0' }}>
                    {voiceNotes.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                        <Mic size={30} style={{ opacity: 0.4 }} />
                        <div style={{ marginTop: 10, fontSize: 13.5, fontWeight: 600 }}>No voice message</div>
                        <div style={{ marginTop: 4, fontSize: 12 }}>
                          A voice message can be recorded when the ticket is raised.
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {voiceNotes.map(att => {
                          // Same relative-URL trick the attachments tab uses, so the
                          // audio loads from whichever host is serving the app.
                          const fileUrl = `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')}${att.file_url}`;
                          const sizeKB = att.file_size ? (att.file_size / 1024).toFixed(1) : '—';
                          const dur = att.duration_seconds
                            ? `${String(Math.floor(att.duration_seconds / 60)).padStart(2, '0')}:${String(att.duration_seconds % 60).padStart(2, '0')}`
                            : null;
                          return (
                            <div key={att.id} style={{
                              padding: '14px 16px', borderRadius: 10,
                              border: '1px solid var(--border)', background: 'var(--bg-card)',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                                <div style={{
                                  width: 34, height: 34, borderRadius: '50%', background: '#ede9fe',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  <Mic size={16} color="var(--primary)" />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-main)' }}>
                                    Voice message{dur ? ` · ${dur}` : ''}
                                  </div>
                                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                                    Recorded by {att.uploaded_by_name || 'Unknown'} · {format(new Date(att.created_at), 'dd MMM yyyy, HH:mm')} · {sizeKB} KB
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <a href={fileUrl} download={att.file_name} target="_blank" rel="noreferrer"
                                    title="Download"
                                    style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-sub)', display: 'flex', alignItems: 'center' }}>
                                    <Download size={14} />
                                  </a>
                                  {(isAdmin || att.uploaded_by === user?.id) && (
                                    <button onClick={() => handleDeleteAttachment(att.id)} title="Delete"
                                      style={{ padding: '5px 7px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                      <Trash2 size={14} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {/* preload=metadata so the browser can show the duration
                                  without pulling the whole clip until Play is pressed. */}
                              <audio controls preload="metadata" src={fileUrl} style={{ width: '100%', height: 40 }}>
                                Your browser cannot play this audio.{' '}
                                <a href={fileUrl} download={att.file_name}>Download it instead.</a>
                              </audio>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── AUDIT TRAIL TAB ── */}
                {activeTab === 'audit' && (
                  <div>
                    {(!ticket.history || ticket.history.length === 0) && (
                      <div className="empty-state">
                        <AlertCircle size={32} color="var(--gray-300)" style={{ marginBottom: 10 }} />
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)' }}>No audit entries</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ticket.history?.map(h => {
                        const resolveVal = (val) => {
                          if (!val || val === 'null') return null;
                          if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(val)) {
                            const c = consultants.find(x => x.id === val);
                            return c ? c.name : val.slice(0, 8) + '…';
                          }
                          return val.replace(/_/g, ' ');
                        };
                        const FIELD_LABELS = {
                          assigned_to: 'Assigned To', status: 'Status', priority: 'Priority',
                          escalated: 'Escalated', title: 'Title', description: 'Description',
                          support_tier: 'Support Tier', impact: 'Impact', urgency: 'Urgency',
                          created: 'Ticket Created', sub_ticket: 'Sub-ticket Added',
                        };
                        const isCreated = h.field_changed === 'created';
                        const oldVal = resolveVal(h.old_value);
                        const newVal = resolveVal(h.new_value);

                        return (
                          <div key={h.id} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: isCreated ? '#f0fdf4' : 'var(--gray-50)', borderRadius: 8, border: `1px solid ${isCreated ? '#86efac' : 'var(--border)'}`, fontSize: 12.5 }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: 700, color: 'var(--text-main)' }}>{h.changed_by_name || 'System'}</span>
                              {isCreated ? (
                                h.new_value ? (
                                  <>
                                    <span style={{ color: 'var(--text-muted)' }}> created this ticket on behalf of </span>
                                    <span style={{ background: '#dcfce7', color: '#16a34a', padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 700 }}>{h.new_value}</span>
                                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> (requestor)</span>
                                  </>
                                ) : (
                                  <span style={{ color: 'var(--text-muted)' }}> created this ticket</span>
                                )
                              ) : (
                                <>
                                  <span style={{ color: 'var(--text-muted)' }}> changed </span>
                                  <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{FIELD_LABELS[h.field_changed] || h.field_changed}</span>
                                  {oldVal && (
                                    <>
                                      <span style={{ color: 'var(--text-muted)' }}> from </span>
                                      <span style={{ background: '#fee2e2', color: '#dc2626', padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 500, textDecoration: 'line-through' }}>{oldVal}</span>
                                    </>
                                  )}
                                  {newVal && (
                                    <>
                                      <span style={{ color: 'var(--text-muted)' }}> to </span>
                                      <span style={{ background: '#dcfce7', color: '#16a34a', padding: '1px 7px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>{newVal}</span>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                            <div style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: 11.5, whiteSpace: 'nowrap' }}>
                              {format(new Date(h.changed_at), 'dd MMM yyyy, HH:mm')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            </div>

            {/* ── SUB-TICKETS ── */}
            {!ticket.parent_ticket_id && (ticket.subTickets?.length > 0 || isAdmin || (isConsultant && ticket.assigned_to === user?.id)) && (
              <div className="card" style={{ padding: '16px 20px', marginTop: 16, borderLeft: '3px solid #7c3aed' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <GitBranch size={15} color="#7c3aed" />
                    <span className="section-heading" style={{ margin: 0, color: '#7c3aed' }}>
                      Sub-tickets
                    </span>
                    {ticket.subTickets?.length > 0 && (
                      <span style={{ background: '#ede9fe', color: '#7c3aed', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                        {ticket.subTickets.length}
                      </span>
                    )}
                  </div>
                  {(isAdmin || (isConsultant && ticket.assigned_to === user?.id)) && (
                    <button className="btn btn-outline btn-sm" style={{ color: '#7c3aed', borderColor: '#c4b5fd', fontSize: 12 }} onClick={() => setSubModal(true)}>
                      <Plus size={13} /> New Sub-ticket
                    </button>
                  )}
                </div>

                {(!ticket.subTickets || ticket.subTickets.length === 0) ? (
                  <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                    No sub-tickets yet. Add one to involve another consultant.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {ticket.subTickets.map(sub => {
                      const sm = STATUS_META[sub.status] || STATUS_META.open;
                      const pm = PRIORITY_META[sub.priority] || PRIORITY_META.medium;
                      return (
                        <div key={sub.id}
                          onClick={() => navigate(`/tickets/${sub.id}`)}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-body)', cursor: 'pointer', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-body)'}
                        >
                          <GitBranch size={13} color="#a78bfa" style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', background: '#ede9fe', padding: '1px 7px', borderRadius: 5 }}>{sub.ticket_number}</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.title}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                              <span style={{ padding: '1px 7px', borderRadius: 5, background: sm.bg, color: sm.color, fontWeight: 600 }}>{sm.label}</span>
                              <span style={{ padding: '1px 7px', borderRadius: 5, background: pm.bg, color: pm.color, fontWeight: 600 }}>{pm.label}</span>
                              {sub.assigned_to_name && <span>→ {sub.assigned_to_name}</span>}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
                            <SLATimer ticketId={sub.id} priority={sub.priority} compact />
                          </div>
                          <ExternalLink size={12} color="var(--text-muted)" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Time logs */}
            {ticket.timeLogs?.length > 0 && (
              <div className="card" style={{ padding: '16px 20px', marginTop: 16 }}>
                <div className="section-heading">Time Logged</div>
                {ticket.timeLogs.map(tl => (
                  <div key={tl.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13 }}>
                    <div>
                      <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{tl.user_name}</span>
                      {tl.work_description && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{tl.work_description}</span>}
                    </div>
                    <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{tl.hours_spent}h</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>
                  <span>Total</span>
                  <span style={{ color: 'var(--primary)' }}>
                    {ticket.timeLogs.reduce((s, tl) => s + parseFloat(tl.hours_spent), 0).toFixed(1)}h
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* ═══ RIGHT PANEL ═══ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* ── ASSIGNED TECHNICIAN ── */}
            <div className="card" style={{ padding: '16px 18px' }}>
              <div className="section-heading">Assigned Technician</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                {ticket.assigned_to_name ? (
                  <>
                    <Av name={ticket.assigned_to_name} role="consultant" size={34} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-main)' }}>{ticket.assigned_to_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Consultant</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={ticket.assigned_to}>
                        {ticket.assigned_to}
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <User size={16} color="var(--gray-400)" />
                    </div>
                    Unassigned
                  </div>
                )}
              </div>

              {/* Reassign - admin and consultant can change */}
              {canEdit && (
                <div>
                  {/* Group filter — pick a group first, then a consultant in it */}
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Group / Module
                  </label>
                  <select
                    className="form-control"
                    style={{ fontSize: 12, marginBottom: 10 }}
                    value={assignGroup}
                    onChange={e => setAssignGroup(e.target.value)}
                    disabled={updating}
                  >
                    <option value="">— All groups —</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.name}{g.consultant_count != null ? ` (${g.consultant_count})` : ''}</option>
                    ))}
                  </select>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {ticket.assigned_to_name ? 'Reassign To' : 'Assign To'}
                  </label>
                  <select
                    className="form-control"
                    style={{ fontSize: 12 }}
                    value={ticket.assigned_to || ''}
                    onChange={e => patch({ assigned_to: e.target.value || null, status: e.target.value ? 'assigned' : 'open' })}
                    disabled={updating}
                  >
                    <option value="">— Unassigned —</option>
                    {consultants
                      .filter(c => !assignGroup || c.group_id === assignGroup)
                      .map(c => (
                      <option key={c.id} value={c.id}>{c.name}{c.group_code ? ` · ${c.group_code}` : (c.department ? ` · ${c.department}` : '')}</option>
                    ))}
                  </select>
                  {ticket.assigned_to_name && (
                    <p style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                      Previous: {ticket.assigned_to_name} — reassignment will be logged
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ── SLA STATUS CARD ── */}
            {slaData && !slaData.no_sla && (
              <div className="card" style={{ padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div className="section-heading" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Shield size={14} color="var(--primary)" /> SLA Status
                  </div>
                  {isAdmin && !['resolved','closed'].includes(ticket.status) && (
                    slaData.is_paused ? (
                      <button
                        onClick={handleSLAResume}
                        disabled={slaUpdating}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid #86efac', background: '#f0fdf4', color: '#16a34a', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        <PlayCircle size={12} /> Resume
                      </button>
                    ) : (
                      <button
                        onClick={handleSLAPause}
                        disabled={slaUpdating}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#f9fafb', color: '#6b7280', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                      >
                        <PauseCircle size={12} /> Pause
                      </button>
                    )
                  )}
                </div>

                {/* Timer */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                  <SLATimer ticketId={id} priority={ticket.priority} />
                </div>

                {/* Info rows */}
                {[
                  { label: 'SLA Target',    value: `${slaData.sla_hours}h (Business)` },
                  { label: 'Due Date',       value: slaData.effective_due ? format(new Date(slaData.effective_due), 'dd MMM yyyy, HH:mm') : '—' },
                  { label: 'Original Due',   value: slaData.original_due  ? format(new Date(slaData.original_due),  'dd MMM yyyy, HH:mm') : '—', hide: !slaData.total_paused_minutes },
                  { label: 'Paused Time',    value: slaData.total_paused_minutes > 0 ? `${Math.floor(slaData.total_paused_minutes/60)}h ${Math.round(slaData.total_paused_minutes%60)}m extended` : '—', hide: !slaData.total_paused_minutes },
                ].filter(r => !r.hide).map(r => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border-light)', fontSize: 11.5 }}>
                    <span style={{ color: 'var(--text-muted)' }}>{r.label}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{r.value}</span>
                  </div>
                ))}

                {slaData.is_paused && (
                  <div style={{ marginTop: 10, padding: '7px 10px', borderRadius: 7, background: '#f3f4f6', fontSize: 11.5, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <PauseCircle size={13} /> SLA is paused (consultant checked out or on leave)
                  </div>
                )}
              </div>
            )}

            {/* ── SET STATUS ── */}
            {canEdit && (
              <div className="card" style={{ padding: '16px 18px' }}>
                <div className="section-heading">Set Status</div>
                {/* Block status change for consultants when SLA is paused (checked out or on leave) */}
                {isConsultant && slaData?.is_paused ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 12px', borderRadius: 8,
                    background: '#fef9ec', border: '1.5px solid #fbbf24',
                    fontSize: 12, color: '#92400e',
                  }}>
                    <span style={{ fontSize: 16 }}>🔒</span>
                    <span>You are checked out / on leave, so status changes are locked. Check in and your tickets will automatically move back to <b>In Progress</b>.</span>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {(isAdmin
                      ? [...STATUS_STEPS, 'reopened']
                      : ['in_progress', 'pending']
                    ).map(s => {
                      const m = STATUS_META[s] || STATUS_META.open;
                      const isCurrent = ticket.status === s;
                      return (
                        <button
                          key={s}
                          onClick={() => !isCurrent && patch({ status: s })}
                          disabled={isCurrent || updating}
                          style={{
                            padding: '7px 10px', borderRadius: 6, border: `1.5px solid ${isCurrent ? m.color : 'var(--border)'}`,
                            background: isCurrent ? m.bg : 'var(--bg-card)',
                            color: isCurrent ? m.color : 'var(--text-sub)',
                            fontSize: 11.5, fontWeight: isCurrent ? 700 : 500,
                            cursor: isCurrent ? 'default' : 'pointer',
                            transition: 'all 0.15s', textAlign: 'center',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          }}
                          onMouseEnter={e => { if (!isCurrent) { e.currentTarget.style.background = m.bg; e.currentTarget.style.color = m.color; e.currentTarget.style.borderColor = m.color; }}}
                          onMouseLeave={e => { if (!isCurrent) { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.color = 'var(--text-sub)'; e.currentTarget.style.borderColor = 'var(--border)'; }}}
                        >
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                          {m.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* User: reopen */}
            {user?.role === 'user' && ticket.status === 'resolved' && (
              <button className="btn btn-outline" style={{ width: '100%', justifyContent: 'center', color: '#dc2626', borderColor: '#fca5a5' }}
                onClick={() => patch({ status: 'reopened' })}>
                <RotateCcw size={14} /> Reopen Ticket
              </button>
            )}

            {/* ── PRIORITY ── */}
            {canEdit && (
              <div className="card" style={{ padding: '16px 18px' }}>
                <div className="section-heading">Priority</div>
                <select
                  className="form-control"
                  style={{ fontSize: 12 }}
                  value={ticket.priority}
                  onChange={e => patch({ priority: e.target.value })}
                  disabled={updating}
                >
                  <option value="critical">P1 — Critical</option>
                  <option value="high">P2 — High</option>
                  <option value="medium">P3 — Medium</option>
                  <option value="low">P4 — Low</option>
                </select>
                {/* SLA info */}
                {ticket.sla_due_at && (
                  <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: isBreached ? '#fee2e2' : '#dcfce7', border: `1px solid ${isBreached ? '#fecaca' : '#bbf7d0'}` }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: isBreached ? '#dc2626' : '#16a34a' }}>
                      {isBreached ? '⚠️ SLA Breached' : '✅ SLA On Track'}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                      Due: {format(new Date(ticket.sla_due_at), 'MMM d, HH:mm')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── TICKET INFO ── */}
            <div className="card" style={{ padding: '16px 18px' }}>
              <div className="section-heading">Ticket Info</div>
              <InfoRow label={ticket.requestor_id ? 'Created By' : 'Raised By'} value={ticket.raised_by_name} />
              {ticket.requestor_id && (
                <InfoRow label="Requestor" value={`${ticket.requestor_name}${ticket.requestor_email ? ` (${ticket.requestor_email})` : ''}`} />
              )}
              <InfoRow label="Assigned To" value={ticket.assigned_to_name || 'Unassigned'} />
              {ticket.support_tier && <InfoRow label="Support Tier" value={ticket.support_tier} />}
              {ticket.impact   && <InfoRow label="Impact"   value={ticket.impact?.charAt(0).toUpperCase() + ticket.impact?.slice(1)} />}
              {ticket.urgency  && <InfoRow label="Urgency"  value={ticket.urgency?.charAt(0).toUpperCase() + ticket.urgency?.slice(1)} />}
              {ticket.site     && <InfoRow label="Site"     value={ticket.site} />}
              {ticket.project_name && <InfoRow label="Client" value={ticket.project_name} />}
              {ticket.parentTicket && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid var(--border-light)', fontSize: 13, cursor: 'pointer' }}
                  onClick={() => navigate(`/tickets/${ticket.parent_ticket_id}`)}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Parent Ticket</span>
                  <span style={{ fontWeight: 700, color: '#7c3aed', fontSize: 12 }}>{ticket.parentTicket.ticket_number}</span>
                </div>
              )}
              {!ticket.parent_ticket_id && ticket.subTickets?.length > 0 && (
                <InfoRow label="Sub-tickets" value={`${ticket.subTickets.length} linked`} />
              )}
              <InfoRow label="Created" value={format(new Date(ticket.created_at), 'dd MMM yyyy, HH:mm')} />
              <InfoRow label="Updated" value={format(new Date(ticket.updated_at), 'dd MMM yyyy, HH:mm')} />
              {ticket.resolved_at && <InfoRow label="Resolved" value={format(new Date(ticket.resolved_at), 'dd MMM yyyy, HH:mm')} />}
            </div>

            {/* ── ESCALATION INFO ── */}
            {ticket.escalated && (
              <div className="card" style={{ padding: '16px 18px', borderColor: '#fca5a5', background: '#fff5f5' }}>
                <div className="section-heading" style={{ color: '#dc2626' }}>🔺 Escalated</div>
                {ticket.escalated_reason && (
                  <p style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.6, marginBottom: 6 }}>{ticket.escalated_reason}</p>
                )}
                {ticket.escalated_at && (
                  <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {format(new Date(ticket.escalated_at), 'dd MMM yyyy, HH:mm')}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ RESOLVE-WITH-KB MODAL ══ */}
      {resolveModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => !resolving && setResolveModal(false)}
        >
          <div
            style={{ background: 'var(--bg-card)', borderRadius: 18, padding: '28px 32px', width: '100%', maxWidth: 580, boxShadow: '0 24px 80px rgba(0,0,0,0.18)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle size={18} color="#16a34a" /> Resolve Ticket
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                  {ticket.ticket_number} — Resolution will be saved to the Knowledge Base automatically.
                </div>
              </div>
              <button onClick={() => !resolving && setResolveModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '16px 0' }} />

            <form onSubmit={handleResolveSubmit}>
              {/* Problem Heading */}
              <div style={{ marginBottom: 14 }}>
                <label className="form-label" style={{ fontWeight: 700 }}>
                  Problem Heading <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Brief title for what the problem was…"
                  value={resolveForm.resolution_heading}
                  onChange={e => setResolveForm(f => ({ ...f, resolution_heading: e.target.value }))}
                  required
                  style={{ fontSize: 13 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>This will become the Knowledge Base article title.</div>
              </div>

              {/* Root Cause */}
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Root Cause</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="What caused the issue? (optional)"
                  value={resolveForm.root_cause}
                  onChange={e => setResolveForm(f => ({ ...f, root_cause: e.target.value }))}
                  style={{ fontSize: 13, resize: 'vertical' }}
                />
              </div>

              {/* Resolution Steps */}
              <div style={{ marginBottom: 14 }}>
                <label className="form-label" style={{ fontWeight: 700 }}>
                  How Was It Resolved? <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  rows={5}
                  placeholder="Step-by-step description of how the issue was resolved…&#10;e.g.&#10;1. Identified the root cause as a misconfigured HRMS module&#10;2. Updated configuration in transaction SM30&#10;3. Ran sync job and verified employee data"
                  value={resolveForm.resolution_steps}
                  onChange={e => setResolveForm(f => ({ ...f, resolution_steps: e.target.value }))}
                  required
                  style={{ fontSize: 13, resize: 'vertical' }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>This detailed explanation will be stored in the Knowledge Base for future reference.</div>
              </div>

              {/* KB Category */}
              <div style={{ marginBottom: 20 }}>
                <label className="form-label">Knowledge Base Category</label>
                <input
                  className="form-control"
                  placeholder={ticket.service_category || ticket.category || 'General'}
                  value={resolveForm.kb_category}
                  onChange={e => setResolveForm(f => ({ ...f, kb_category: e.target.value }))}
                  style={{ fontSize: 13 }}
                />
              </div>

              {/* Info box */}
              <div style={{ padding: '12px 16px', borderRadius: 10, background: 'var(--primary-50, #f5f3ff)', border: '1px solid var(--primary-light)', fontSize: 12, color: 'var(--text-sub)', marginBottom: 20 }}>
                <strong style={{ color: 'var(--primary)', fontSize: 13 }}>What happens on submit:</strong>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Ticket resolved */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckCircle size={13} color="#16a34a" />
                    </span>
                    <span>Ticket status → <strong>Resolved</strong></span>
                  </div>
                  {/* KB saved */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <CheckCircle size={13} color="#16a34a" />
                    </span>
                    <span>Resolution saved to <strong>Knowledge Base</strong> automatically</span>
                  </div>
                  {/* User notified — FIRST, most important */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa' }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#ffedd5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>🔔</span>
                    <span>
                      <strong style={{ color: '#9a3412' }}>
                        {ticket.requestor_id ? ticket.requestor_name : ticket.raised_by_name}
                      </strong>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>(ticket requester)</span>
                      <span style={{ color: '#9a3412', marginLeft: 4 }}>— notified immediately</span>
                    </span>
                  </div>
                  {/* Admins notified */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 13 }}>🔔</span>
                    <span>
                      <strong style={{ color: '#0c4a6e' }}>All Admins</strong>
                      <span style={{ color: '#0c4a6e', marginLeft: 4 }}>— notified immediately</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setResolveModal(false)} disabled={resolving}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-sm"
                  disabled={resolving || !resolveForm.resolution_heading.trim() || !resolveForm.resolution_steps.trim()}
                  style={{ background: '#16a34a', color: '#fff', border: 'none', gap: 6, minWidth: 140 }}
                >
                  {resolving ? (
                    <><div className="spinner" style={{ width: 13, height: 13, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> Resolving…</>
                  ) : (
                    <><CheckCircle size={14} /> Submit & Resolve</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── SUB-TICKET MODAL ── */}
      {subModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setSubModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: 520, boxShadow: 'var(--shadow-lg)', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <div style={{ background: '#ede9fe', borderRadius: 8, padding: 7, display: 'flex' }}>
                <GitBranch size={18} color="#7c3aed" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-main)' }}>Create Sub-ticket</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Under <span style={{ fontWeight: 600, color: '#7c3aed' }}>{ticket.ticket_number}</span> — each sub-ticket has its own SLA &amp; consultant
                </div>
              </div>
              <button onClick={() => setSubModal(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={18} color="var(--text-muted)" />
              </button>
            </div>

            {/* Title */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', display: 'block', marginBottom: 5 }}>Title <span style={{ color: '#dc2626' }}>*</span></label>
              <input
                className="form-control"
                value={subForm.title}
                onChange={e => setSubForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Describe the sub-task..."
                style={{ fontSize: 13 }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', display: 'block', marginBottom: 5 }}>Description <span style={{ color: '#dc2626' }}>*</span></label>
              <textarea
                className="form-control"
                rows={3}
                value={subForm.description}
                onChange={e => setSubForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What needs to be done in this sub-ticket?"
                style={{ fontSize: 13, resize: 'vertical' }}
              />
            </div>

            {/* Type + Priority */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', display: 'block', marginBottom: 5 }}>Type</label>
                <select className="form-control" style={{ fontSize: 13 }} value={subForm.ticket_type} onChange={e => setSubForm(f => ({ ...f, ticket_type: e.target.value }))}>
                  <option value="incident">Incident</option>
                  <option value="service_request">Service Request</option>
                  <option value="change_request">Change Request</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', display: 'block', marginBottom: 5 }}>Priority</label>
                <select className="form-control" style={{ fontSize: 13 }} value={subForm.priority} onChange={e => setSubForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="critical">P1 — Critical</option>
                  <option value="high">P2 — High</option>
                  <option value="medium">P3 — Medium</option>
                  <option value="low">P4 — Low</option>
                </select>
              </div>
            </div>

            {/* Assign to consultant */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-sub)', display: 'block', marginBottom: 5 }}>Assign to Consultant</label>
              <select className="form-control" style={{ fontSize: 13 }} value={subForm.assigned_to} onChange={e => setSubForm(f => ({ ...f, assigned_to: e.target.value }))}>
                <option value="">— Unassigned —</option>
                {consultants.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.department ? ` · ${c.department}` : ''}</option>
                ))}
              </select>
              {subForm.assigned_to && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4 }}>
                  ✓ This sub-ticket will have its own SLA starting from assignment time.
                </div>
              )}
            </div>

            {/* Info box */}
            <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: '#6b21a8' }}>
              <strong>How sub-tickets work:</strong> Each sub-ticket gets its own ticket number (e.g. <strong>{ticket.ticket_number}-C1</strong>), separate SLA timer, and can be assigned to a different consultant — ideal when multiple teams need to work on the same issue.
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setSubModal(false)}>Cancel</button>
              <button
                className="btn btn-sm"
                style={{ background: '#7c3aed', color: '#fff', border: 'none' }}
                onClick={handleCreateSubTicket}
                disabled={subSubmitting || !subForm.title.trim() || !subForm.description.trim()}
              >
                {subSubmitting ? 'Creating…' : <><GitBranch size={13} /> Create Sub-ticket</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ESCALATE MODAL ── */}
      {escalateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setEscalateModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: 440, boxShadow: 'var(--shadow-lg)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-main)', marginBottom: 6 }}>Escalate Ticket</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Provide a reason for escalation. Admins will be notified.</div>
            <textarea
              className="form-control"
              rows={4} value={escalateReason}
              onChange={e => setEscalateReason(e.target.value)}
              placeholder="Reason for escalation…"
              style={{ marginBottom: 16, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setEscalateModal(false)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={handleEscalate} disabled={!escalateReason.trim()}>
                <AlertTriangle size={13} /> Escalate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation — admin only. Deliberately states that this is
          recoverable, so it doesn't read like a destructive action. */}
      {deleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDeleteModal(false)}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 28, width: 440, boxShadow: 'var(--shadow-lg)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-main)', marginBottom: 6 }}>Delete Ticket</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-main)' }}>{ticket.ticket_number}</strong> will be moved to the
              Recycle Bin. It disappears from ticket lists, reports and SLA, but nothing is
              permanently erased — you can restore it from <strong style={{ color: 'var(--text-main)' }}>Ticket
              History → Deleted Tickets</strong>.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setDeleteModal(false)} disabled={deleting}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting}>
                <Trash2 size={13} /> {deleting ? 'Deleting…' : 'Move to Recycle Bin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Ticket — admin-only full edit form, pre-filled with current data */}
      {showEdit && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)', overflowY: 'auto', padding: '24px 0' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowEdit(false); }}>
          <div style={{ maxWidth: 920, margin: '0 auto', background: 'var(--bg-main, #fff)', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.35)', padding: '24px 28px' }}
            onClick={(e) => e.stopPropagation()}>
            <NewTicketPage
              embedded
              editTicket={ticket}
              onClose={() => setShowEdit(false)}
              onUpdated={() => { setShowEdit(false); fetchTicket(); }}
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
}
